/**
 * useMessageTemplates — shared hook that both BulkWhatsappModal and
 * LeadTemplates consume to read the backend catalog. Deliberately thin:
 * one fetch per mount + on demand via `reload`, no polling, no shared
 * cache. Each composer mounts the hook fresh so admin edits show up as
 * soon as the composer is re-opened.
 *
 * `fallbackUsed` is the single signal the callers watch to decide whether
 * to render backend templates or the legacy local ones:
 *   - Network / auth error  → fallbackUsed=true, error set, empty array.
 *   - Backend returns []    → fallbackUsed=true, backendEmpty=true, error empty.
 *   - Success + non-empty   → fallbackUsed=false, templates populated.
 *
 * The distinction lets the UI show two different banners without every
 * consumer duplicating the try/catch scaffolding.
 */
import { useCallback, useEffect, useState } from "react";
import { listMessageTemplates } from "@/lib/messageTemplatesApi";
import { formatError } from "@/lib/api";

export function useMessageTemplates({ channel = "whatsapp", enabled = true } = {}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [backendEmpty, setBackendEmpty] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setBackendEmpty(false);
    listMessageTemplates({ channel, includeArchived: false })
      .then((rows) => {
        if (cancelled) return;
        setTemplates(rows);
        setBackendEmpty(rows.length === 0);
      })
      .catch((e) => {
        if (cancelled) return;
        setTemplates([]);
        setError(formatError(e));
        setBackendEmpty(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [channel, enabled, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  // fallbackUsed collapses "hard error" and "empty catalog" into a single
  // signal for consumers that just need to know whether to render local
  // fallback content. Callers that need to differentiate messages check
  // `error` (hard error) vs `backendEmpty` (200 but nothing to render).
  const fallbackUsed = !!error || backendEmpty;

  return { templates, loading, error, backendEmpty, fallbackUsed, reload };
}
